/**
 * Shift API Contract
 *
 * Defines the oRPC contract for Shifts - scheduling, assignments, and shift management.
 *
 * IMPORTANT SECURITY NOTES:
 * - Company-scoped endpoints (getShifts, getShiftsByCompany, getMyShifts, createShift)
 *   do NOT include companyId in path or input - it comes from JWT context
 * - Batch publish gets companyId from employment lookup (existing pattern)
 *
 * ROUTE ORDERING:
 * - Specific routes (/my, /company, /batch-publish, etc.) MUST come before /{id}
 *   to prevent "my" or "company" from matching as UUID
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateShiftInputSchema, UpdateShiftInputSchema, ShiftResponseSchema, MyShiftResponseSchema, BatchPublishInputSchema, BatchPublishResponseSchema, AssignEmployeeInputSchema, ShiftMatesResponseSchema, CreateRetroactiveShiftInputSchema, RetroactiveShiftResponseSchema, } from "@hourly/api/shift";
import { CreateUnplannedShiftRequestSchema, UnplannedShiftResponseSchema } from "@hourly/api/unplannedShift";
import { EarlyClockInRequestSchema, EarlyClockInResponseSchema } from "@hourly/api/earlyClockIn";
import { AssignSkillsToShiftInputSchema, BaseSkillResponseSchema, } from "@hourly/api/skills";
import { ApprovalRequestResponseSchema, RespondToApprovalRequestInputSchema, } from "@hourly/api/approvalRequest";
// ============================================
// Query Contracts
// ============================================
/**
 * GET /shifts
 * Get shifts with optional date range filter
 * Note: companyId comes from JWT, not query params
 */
const getShifts = oc
    .route({
    method: "GET",
    path: "/shifts",
    summary: "Get all shifts for the current company",
    tags: ["Shifts"],
})
    .input(z.object({
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}))
    .output(z.array(ShiftResponseSchema));
/**
 * GET /shifts/my
 * Get shifts for the current authenticated user
 * Uses JWT userId to find employment, then returns assigned shifts
 * Note: companyId comes from JWT, not query params
 * MUST be before /shifts/{id} to prevent "my" matching as UUID
 */
const getMyShifts = oc
    .route({
    method: "GET",
    path: "/shifts/my",
    summary: "Get my shifts (authenticated user)",
    tags: ["Shifts"],
})
    .input(z.object({
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}))
    .output(z.array(MyShiftResponseSchema));
/**
 * GET /shifts/available
 * Get shifts available for pickup by current employee
 * Uses JWT userId to find employment, then returns eligible open shifts
 * MUST be before /shifts/{id} to prevent "available" matching as UUID
 */
const getAvailableShifts = oc
    .route({
    method: "GET",
    path: "/shifts/available",
    summary: "Get shifts available for pickup by current employee",
    tags: ["Shifts"],
})
    .input(z.object({
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}))
    .output(z.array(ShiftResponseSchema));
/**
 * GET /shifts/company
 * Get all shifts for the current company (companyId from JWT)
 * Note: companyId comes from JWT, NOT path parameter
 * MUST be before /shifts/{id} to prevent "company" matching as UUID
 */
const getShiftsByCompany = oc
    .route({
    method: "GET",
    path: "/shifts/company",
    summary: "Get all shifts for the current company",
    tags: ["Shifts"],
})
    .input(z.object({
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}))
    .output(z.array(ShiftResponseSchema));
/**
 * GET /shifts/employee/{employeeId}
 * Get all shifts for a specific employee with optional date range
 */
const getShiftsByEmployee = oc
    .route({
    method: "GET",
    path: "/shifts/employee/{employeeId}",
    summary: "Get all shifts for an employee",
    tags: ["Shifts"],
})
    .input(z.object({
    employeeId: z.uuid(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}))
    .output(z.array(ShiftResponseSchema));
/**
 * GET /shifts/location/{locationId}
 * Get all shifts for a specific location with optional date range
 */
const getShiftsByLocation = oc
    .route({
    method: "GET",
    path: "/shifts/location/{locationId}",
    summary: "Get all shifts for a location",
    tags: ["Shifts"],
})
    .input(z.object({
    locationId: z.uuid(),
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}))
    .output(z.array(ShiftResponseSchema));
/**
 * GET /shifts/{id}
 * Get a single shift by ID
 */
const getShift = oc
    .route({
    method: "GET",
    path: "/shifts/{id}",
    summary: "Get shift by ID",
    tags: ["Shifts"],
})
    .input(z.object({ id: z.uuid() }))
    .output(ShiftResponseSchema);
// ============================================
// Create/Update/Delete Contracts
// ============================================
/**
 * POST /shifts/retroactive
 * Create a retroactive shift with pre-filled approved timecard
 * Note: companyId will be injected from JWT context in handler
 * SECURITY: companyId comes from JWT, NOT request body
 * MUST be before /shifts/{id} to prevent "retroactive" matching as UUID
 */
const createRetroactiveShift = oc
    .route({
    method: "POST",
    path: "/shifts/retroactive",
    summary: "Create a retroactive shift with pre-filled approved timecard",
    tags: ["Shifts"],
    successStatus: 201,
})
    .input(CreateRetroactiveShiftInputSchema)
    .output(RetroactiveShiftResponseSchema);
/**
 * POST /shifts
 * Create a new shift
 * Note: companyId will be injected from JWT context in handler
 * SECURITY: CreateShiftInputSchema already excludes companyId (comes from JWT)
 */
const createShift = oc
    .route({
    method: "POST",
    path: "/shifts",
    summary: "Create a new shift",
    tags: ["Shifts"],
    successStatus: 201,
})
    .input(CreateShiftInputSchema)
    .output(ShiftResponseSchema);
/**
 * PATCH /shifts/{id}
 * Update an existing shift
 */
const updateShift = oc
    .route({
    method: "PATCH",
    path: "/shifts/{id}",
    summary: "Update shift",
    tags: ["Shifts"],
})
    .input(z.object({ id: z.uuid() }).merge(UpdateShiftInputSchema))
    .output(ShiftResponseSchema);
/**
 * DELETE /shifts/{id}
 * Delete a shift
 */
const deleteShift = oc
    .route({
    method: "DELETE",
    path: "/shifts/{id}",
    summary: "Delete shift",
    tags: ["Shifts"],
    successStatus: 204,
})
    .input(z.object({ id: z.uuid() }))
    .output(z.void());
// ============================================
// Assignment Contracts
// ============================================
/**
 * PATCH /shifts/{id}/assign
 * Assign an employee to a shift
 */
const assignEmployee = oc
    .route({
    method: "PATCH",
    path: "/shifts/{id}/assign",
    summary: "Assign employee to shift",
    tags: ["Shifts"],
})
    .input(z.object({ id: z.uuid() }).merge(AssignEmployeeInputSchema))
    .output(ShiftResponseSchema);
/**
 * PATCH /shifts/{id}/unassign
 * Unassign an employee from a shift
 */
const unassignEmployee = oc
    .route({
    method: "PATCH",
    path: "/shifts/{id}/unassign",
    summary: "Unassign employee from shift",
    tags: ["Shifts"],
})
    .input(z.object({ id: z.uuid() }))
    .output(ShiftResponseSchema);
/**
 * POST /shifts/{id}/accept
 * Accept an open shift (self-assign as employee)
 * Note: userId comes from JWT context
 */
const acceptShift = oc
    .route({
    method: "POST",
    path: "/shifts/{id}/accept",
    summary: "Accept an open shift",
    tags: ["Shifts"],
})
    .input(z.object({ id: z.uuid() }))
    .output(ShiftResponseSchema);
// ============================================
// Shift Request Contracts
// ============================================
/**
 * POST /shift-requests/pickup
 * Request to pick up an open shift
 * Note: companyId and requesterId come from JWT context
 */
const createPickupRequest = oc
    .route({
    method: "POST",
    path: "/shift-requests/pickup",
    summary: "Request to pick up an open shift",
    tags: ["Shift Requests"],
    successStatus: 201,
})
    .input(z.object({
    shiftId: z.uuid(),
    comment: z.string().optional(),
}))
    .output(z.object({
    approvalRequest: ApprovalRequestResponseSchema,
}));
/**
 * POST /shift-requests/drop
 * Request to drop an assigned shift
 * Note: companyId and requesterId come from JWT context
 */
const createDropRequest = oc
    .route({
    method: "POST",
    path: "/shift-requests/drop",
    summary: "Request to drop an assigned shift",
    tags: ["Shift Requests"],
    successStatus: 201,
})
    .input(z.object({
    shiftId: z.uuid(),
    employeeId: z.uuid(),
    comment: z.string().optional(),
}))
    .output(z.object({
    approvalRequest: ApprovalRequestResponseSchema,
    shift: ShiftResponseSchema,
}));
/**
 * POST /shift-requests/swap
 * Request to swap shift with another employee
 * Note: companyId and requesterId come from JWT context
 * fromEmployeeId comes from JWT (requester's employment)
 */
const createSwapRequest = oc
    .route({
    method: "POST",
    path: "/shift-requests/swap",
    summary: "Request to swap shift with multiple employees",
    tags: ["Shift Requests"],
    successStatus: 201,
})
    .input(z.object({
    shiftId: z.uuid(),
    toEmployeeIds: z.array(z.uuid()).min(1).max(10),
    comment: z.string().optional(),
}))
    .output(z.object({
    approvalRequests: z.array(ApprovalRequestResponseSchema),
}));
;
const getEligibleSwapRecipients = oc
    .route({
    method: "GET",
    path: "/shifts/{shiftId}/eligible-swap-recipients",
    summary: "Get employees eligible to receive a swap request",
    tags: ["Shift Requests"],
})
    .input(z.object({ shiftId: z.uuid() }))
    .output(z.object({
    employees: z.array(z.object({
        id: z.uuid(), // userId - used as toEmployeeId
        employmentId: z.uuid(),
        firstName: z.string(),
        lastName: z.string(),
        photoUrl: z.string().nullable(),
        jobRoles: z.array(z.object({
            id: z.uuid(),
            name: z.string(),
        })),
    })),
}));
/**
 * POST /shift-requests/swap/{id}/respond
 * Respond to a swap request as the targeted recipient
 */
const respondToSwapRequest = oc
    .route({
    method: "POST",
    path: "/shift-requests/swap/{id}/respond",
    summary: "Respond to a swap request as the targeted recipient",
    tags: ["Shift Requests"],
})
    .input(z.object({ id: z.uuid() }).merge(RespondToApprovalRequestInputSchema))
    .output(ApprovalRequestResponseSchema);
// ============================================
// Batch Operations
// ============================================
/**
 * POST /shifts/batch-publish
 * Batch publish schedule changes (creates, updates, deletes, assignments)
 * Note: companyId comes from employment lookup (existing pattern)
 * MUST be before /shifts/{id} to prevent "batch-publish" matching as UUID
 */
const batchPublish = oc
    .route({
    method: "POST",
    path: "/shifts/batch-publish",
    summary: "Batch publish schedule changes",
    tags: ["Shifts"],
})
    .input(BatchPublishInputSchema)
    .output(BatchPublishResponseSchema);
/**
 * POST /shifts/copy-previous-week
 * Copy shifts from previous week to a target week
 * STUB: Returns empty array - full implementation pending
 * MUST be before /shifts/{id} to prevent "copy-previous-week" matching as UUID
 */
const copyPreviousWeek = oc
    .route({
    method: "POST",
    path: "/shifts/copy-previous-week",
    summary: "Copy shifts from previous week (stub)",
    tags: ["Shifts"],
})
    .input(z.object({
    targetWeekStart: z.iso.datetime(),
}))
    .output(z.object({
    success: z.boolean(),
    data: z.array(ShiftResponseSchema),
    message: z.string(),
}));
/**
 * POST /shifts/generate
 * Generate optimal schedule based on sales data and business rules
 * STUB: Returns empty array - full implementation pending
 * MUST be before /shifts/{id} to prevent "generate" matching as UUID
 */
const generateSchedule = oc
    .route({
    method: "POST",
    path: "/shifts/generate",
    summary: "Generate optimal schedule (stub)",
    tags: ["Shifts"],
})
    .input(z.object({
    targetWeekStart: z.iso.datetime(),
}))
    .output(z.object({
    success: z.boolean(),
    data: z.array(ShiftResponseSchema),
    message: z.string(),
}));
// ============================================
// Shift Skills Contracts
// ============================================
/**
 * POST /shifts/{shiftId}/skills
 * Assign multiple skills to a shift (bulk add)
 */
const assignSkillsToShift = oc
    .route({
    method: "POST",
    path: "/shifts/{shiftId}/skills",
    summary: "Assign skills to shift",
    tags: ["Shifts", "Skills"],
})
    .input(z.object({ shiftId: z.uuid() }).merge(AssignSkillsToShiftInputSchema))
    .output(z.object({ message: z.string() }));
/**
 * GET /shifts/{shiftId}/skills
 * Get all skills assigned to a shift
 */
const getShiftSkills = oc
    .route({
    method: "GET",
    path: "/shifts/{shiftId}/skills",
    summary: "Get shift skills",
    tags: ["Shifts", "Skills"],
})
    .input(z.object({ shiftId: z.uuid() }))
    .output(z.array(BaseSkillResponseSchema));
/**
 * DELETE /shifts/{shiftId}/skills/{baseSkillId}
 * Remove a specific skill from a shift
 */
const removeSkillFromShift = oc
    .route({
    method: "DELETE",
    path: "/shifts/{shiftId}/skills/{baseSkillId}",
    summary: "Remove skill from shift",
    tags: ["Shifts", "Skills"],
})
    .input(z.object({
    shiftId: z.uuid(),
    baseSkillId: z.uuid(),
}))
    .output(z.object({ message: z.string() }));
// ============================================
// Unplanned Shift Contracts
// ============================================
/**
 * POST /shifts/my/unplanned
 * Request to clock in without a scheduled shift
 * Note: companyId and employeeId come from JWT context
 * MUST be before /shifts/{id} to prevent "my" matching as UUID
 */
const createUnplannedShift = oc
    .route({
    method: "POST",
    path: "/shifts/my/unplanned",
    summary: "Request to clock in without a scheduled shift",
    tags: ["Shifts"],
    successStatus: 201,
})
    .input(CreateUnplannedShiftRequestSchema)
    .output(UnplannedShiftResponseSchema);
/**
 * POST /shifts/my/early-clock-in
 * Request to clock in early for an existing scheduled shift
 * Note: companyId and employeeId come from JWT context
 * MUST be before /shifts/{id} to prevent "my" matching as UUID
 */
const earlyClockIn = oc
    .route({
    method: "POST",
    path: "/shifts/my/early-clock-in",
    summary: "Request to clock in early for an existing scheduled shift",
    tags: ["Shifts"],
    successStatus: 201,
})
    .input(EarlyClockInRequestSchema)
    .output(EarlyClockInResponseSchema);
const getShiftMates = oc
    .route({ method: "GET", path: "/shifts/{shiftId}/shift-mates" })
    .input(z.object({ shiftId: z.uuid() }))
    .output(ShiftMatesResponseSchema);
// ============================================
// Export Contract
// ============================================
export const shiftContract = {
    // Query operations (order matters!)
    list: getShifts,
    my: getMyShifts,
    available: getAvailableShifts,
    shiftMates: getShiftMates,
    getByCompany: getShiftsByCompany,
    getByEmployee: getShiftsByEmployee,
    getByLocation: getShiftsByLocation,
    get: getShift,
    // Create/Update/Delete
    create: createShift,
    createRetroactive: createRetroactiveShift,
    update: updateShift,
    delete: deleteShift,
    // Assignment operations
    assign: assignEmployee,
    unassign: unassignEmployee,
    accept: acceptShift,
    // Shift request operations
    createPickupRequest: createPickupRequest,
    createDropRequest: createDropRequest,
    createSwapRequest: createSwapRequest,
    getEligibleSwapRecipients: getEligibleSwapRecipients,
    respondToSwapRequest: respondToSwapRequest,
    // Batch operations
    batchPublish: batchPublish,
    copyPreviousWeek: copyPreviousWeek,
    generate: generateSchedule,
    // Skill operations
    assignSkills: assignSkillsToShift,
    getSkills: getShiftSkills,
    removeSkill: removeSkillFromShift,
    // Unplanned shift operations
    createUnplanned: createUnplannedShift,
    earlyClockIn: earlyClockIn,
};
//# sourceMappingURL=shift.js.map